import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import TrafficLightFlash from '../components/TrafficLightFlash';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lesson-card-manager/artifacts/zd0x6jcg_c982a405-739a-4717-a6a0-d9774dc13353_20251118_231830_0000.png";

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [flashing, setFlashing] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password, rememberMe);
        setLoading(false);

        if (result.success) {
            setFlashing(true);
            setTimeout(() => navigate('/dashboard'), 1500);
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="login-split">
            {flashing && <TrafficLightFlash />}
            {/* Hero section - Dark with gold accents */}
            <div 
                className="login-hero relative flex items-center justify-center"
                style={{
                    backgroundColor: '#1A202C'
                }}
            >
                <div className="relative z-10 text-center p-8">
                    <img 
                        src={LOGO_URL} 
                        alt="Rijschool AutoPilot" 
                        className="mx-auto mb-6 w-64 h-64 object-contain"
                    />
                    <p className="text-lg text-gray-400 max-w-md italic">
                        "Drive like a pilot"
                    </p>
                </div>
            </div>

            {/* Login form - Dark theme */}
            <div className="flex items-center justify-center p-8 bg-[#1A202C]">
                <div className="w-full max-w-md">
                    <div className="bg-[#2D3748] p-8 rounded-lg border border-[#4A5568]">
                        <div className="mb-8">
                            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Inloggen
                            </h2>
                            <p className="text-gray-400 mt-2">
                                Welkom terug! Log in om door te gaan.
                            </p>
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-md text-red-300 text-sm" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-gray-300">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="jouw@email.nl"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500 focus:ring-2 focus:ring-[#D4A537] focus:border-[#D4A537]"
                                    data-testid="login-email-input"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-gray-300">Wachtwoord</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="bg-[#1A202C] border-[#4A5568] text-white placeholder:text-gray-500 focus:ring-2 focus:ring-[#D4A537] focus:border-[#D4A537] pr-10"
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                                    >
                                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold btn-ripple tap-bounce"
                                disabled={loading}
                                data-testid="login-submit-button"
                            >
                                {loading ? 'Bezig met inloggen...' : 'Inloggen'}
                            </Button>

                            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none mt-2">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="w-4 h-4 rounded border-[#4A5568] bg-[#1A202C] text-[#D4A537] focus:ring-[#D4A537] cursor-pointer"
                                    data-testid="remember-me-checkbox"
                                />
                                Ingelogd blijven (90 dagen)
                            </label>
                        </form>

                        <div className="mt-4 text-center">
                            <Link to="/forgot-password" className="text-gray-500 text-sm hover:text-[#D4A537]" data-testid="forgot-password-link">
                                Wachtwoord vergeten?
                            </Link>
                        </div>

                        <div className="mt-5 pt-4 border-t border-[#4A5568] text-center">
                            <div className="text-white font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Wil je ook AutoPilot worden?
                            </div>
                            <div className="text-xs text-gray-400 mb-3">Meld je aan bij je rijschool via de app</div>
                            <Link
                                to="/register"
                                data-testid="register-link"
                                className="inline-block w-full bg-[#D4A537] hover:bg-[#E5B84A] text-[#1A202C] font-bold py-2.5 rounded-lg transition-colors"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                            >
                                Registreer nu
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
